import React, { useContext, useEffect, useState } from "react"
import { observer } from "mobx-react"
import { Anchor, Button, Group, Loader, Stack, Text, Title } from "@mantine/core"
import { IconArrowLeft, IconArrowRight, IconCircleCheck, IconMarkdown } from "@tabler/icons-react"
import { AppWorkspaceContext } from "../../stores/AppWorkspace"
import { LANDING_COPY } from "../../../docs/catalog"
import { PROCESSORS, processorHeadline, type Processor } from "../../../processors/catalog"
import { processorPath } from "../../helpers/constants"
import { MarkdownDoc } from "../MarkdownDoc"
import { ProcessorCard } from "./ProcessorCard"
import { ProcessorWizard } from "./ProcessorWizard"
import { MobileHandoff } from "../MobileHandoff"

// /processors and /processors/<id>. The index lists every row in
// config/processors.json, planned ones as "Coming next", then asks which
// other processor the reader uses. A processor page is
// a header, the wizard that hands out its agent directions, then its article.
// ProcessorPage (Rails) renders the same facts into the server body.
export const ProcessorsPage: React.FC = observer(() => {
  const workspace = useContext(AppWorkspaceContext)
  if (!workspace.processorId) return <ProcessorsIndexPage />
  if (!workspace.processor) return <UnknownProcessor id={workspace.processorId} />
  return <ProcessorDetail processor={workspace.processor} />
})

const ProcessorsIndexPage: React.FC = () => {
  const copy = LANDING_COPY.processors
  return (
    <Stack gap={40} py="md" pb="xl" className="or-processors-index">
      <div>
        <Text className="or-eyebrow">{copy.eyebrow}</Text>
        <Title order={1} className="or-integration-title or-platforms-index-title">
          {copy.indexHeadline}
        </Title>
        <Text className="or-lede or-integration-lede">{copy.indexLede}</Text>
      </div>
      <section className="or-frameworks" aria-label={copy.eyebrow}>
        <ul className="or-framework-cards">
          {PROCESSORS.map((processor) => (
            <ProcessorCard key={processor.id} processor={processor} />
          ))}
        </ul>
      </section>
      <Text c="dimmed">
        {copy.otherProcessor}{" "}
        <Anchor href="/contact">
          {copy.otherProcessorLink} <IconArrowRight size={14} aria-hidden="true" />
        </Anchor>
      </Text>
    </Stack>
  )
}

// The server answered 404 for this id already (a planned row or a typo); this
// is only what the SPA draws if it navigated here client-side.
const UnknownProcessor: React.FC<{ id: string }> = observer(({ id }) => {
  const workspace = useContext(AppWorkspaceContext)
  return (
    <Stack gap="md" py="xl">
      <Title order={1}>No processor page for “{id}”</Title>
      <Group>
        <Button variant="light" onClick={() => workspace.goProcessors()}>
          {LANDING_COPY.processors.allProcessors}
        </Button>
      </Group>
    </Stack>
  )
})

// The article is the page's .md twin, fetched rather than bundled: Rails
// serves it from lib/site_pages, the same bytes an agent reads.
const useArticle = (markdownPath: string): string | null => {
  const [article, setArticle] = useState<{ path: string; body: string } | null>(null)
  useEffect(() => {
    const controller = new AbortController()
    fetch(markdownPath, { signal: controller.signal })
      .then((response) => (response.ok ? response.text() : Promise.reject(new Error(`${response.status}`))))
      .then((body) => setArticle({ path: markdownPath, body }))
      .catch((error: unknown) => {
        if (!controller.signal.aborted) console.error(error, { tags: { where: "ProcessorsPage" } })
      })
    return () => controller.abort()
  }, [markdownPath])
  return article?.path === markdownPath ? article.body : null
}

const ProcessorDetail: React.FC<{ processor: Processor }> = observer(({ processor }) => {
  const workspace = useContext(AppWorkspaceContext)
  const copy = LANDING_COPY.processors
  const markdownPath = `${processorPath(processor.id)}.md`
  const article = useArticle(markdownPath)

  return (
    <Stack gap={40} className="or-integration or-processor-page" pb="xl">
      <section className="or-platform-hero">
        <Button
          variant="subtle"
          size="compact-sm"
          leftSection={<IconArrowLeft size={14} />}
          onClick={() => workspace.goProcessors()}
          mb="md"
        >
          {copy.allProcessors}
        </Button>
        <Text className="or-eyebrow">{copy.eyebrow}</Text>
        <Title order={1} className="or-integration-title">
          {processorHeadline(processor)}
        </Title>
        <Text className="or-lede or-integration-lede">{processor.summary}</Text>
        {processor.production ? (
          <div className="or-production-claim">
            <IconCircleCheck className="or-production-claim-icon" size={32} aria-hidden="true" />
            <div>
              <Title order={2} className="or-production-claim-title">
                {processor.production}
              </Title>
              {processor.productionDetail ? (
                <Text className="or-production-claim-detail">{processor.productionDetail}</Text>
              ) : null}
            </div>
          </div>
        ) : null}
        {/* /stripe is the one processor short link (config/routes.rb). */}
        <MobileHandoff
          name={processor.label}
          shortPath={processor.id === "stripe" ? "/stripe" : processorPath(processor.id)}
          pageKind="processor"
          pageId={processor.id}
        />
      </section>

      <ProcessorWizard processor={processor} />

      <article className="or-platform-guide">
        <Group justify="flex-end" mb="md">
          <Button
            component="a"
            href={markdownPath}
            variant="default"
            size="xs"
            leftSection={<IconMarkdown size={14} />}
            title="This page as raw markdown, for a coding agent"
          >
            Markdown
          </Button>
        </Group>
        {article ? <MarkdownDoc markdown={article.replace(/^# [^\n]+\n+/, "")} /> : <Loader size="sm" />}
      </article>
    </Stack>
  )
})
